var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Download, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';
// Set up PDF.js worker - using CDN for reliability
if (typeof window !== 'undefined' && !pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
}
/**
 * Overflow warning component for PDF Preview Mode
 * Detects if content exceeds usable page height
 */
const OverflowWarning = () => {
    const [overflow, setOverflow] = useState(0);
    useEffect(() => {
        const detectOverflow = () => {
            // Find the book-page content
            const bookPage = document.querySelector('.book-page');
            if (bookPage) {
                const contentHeight = bookPage.scrollHeight;
                const maxHeight = 960; // 7×10" page height
                const paddingTotal = 96; // 48px top + 48px bottom
                const usableHeight = maxHeight - paddingTotal; // 864px
                const overflowAmount = Math.max(0, contentHeight - maxHeight);
                setOverflow(overflowAmount);
            }
        };
        // Detect on mount and after short delay (for images/fonts to load)
        detectOverflow();
        const timer = setTimeout(detectOverflow, 200);
        return () => clearTimeout(timer);
    }, []);
    if (overflow > 0) {
        return (_jsxs("div", { style: {
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(239, 68, 68, 0.95)',
                color: 'white',
                padding: '12px',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '14px',
                zIndex: 100,
                borderTop: '3px solid #dc2626'
            }, children: ["\u26A0\uFE0F OVERFLOW: +", overflow, "px will be TRUNCATED in PDF export"] }));
    }
    return (_jsx("div", { style: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(34, 197, 94, 0.9)',
            color: 'white',
            padding: '8px',
            textAlign: 'center',
            fontWeight: '600',
            fontSize: '12px',
            zIndex: 100
        }, children: "\u2713 Page fits within PDF boundaries" }));
};
const BookViewer = ({ pdfUrl, title, coverImage, backCoverImage, pages, showSpreads = true }) => {
    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1);
    const [loading, setLoading] = useState(!!pdfUrl); // Only load if PDF URL provided
    const [error, setError] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [pdfPreviewMode, setPdfPreviewMode] = useState(false);
    const containerRef = useRef(null);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    useEffect(() => {
        // If using custom pages, set loading to false immediately
        if (pages && pages.length > 0) {
            setLoading(false);
            setNumPages(pages.length);
        }
    }, [pages]);
    // Detect mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    useEffect(() => {
        // Dynamically calculate scale to fit A4 pages in viewport
        const calculateScale = () => {
            if (!containerRef.current)
                return;
            const container = containerRef.current;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            // 7×10 inches dimensions
            // Convert to pixels at 96 DPI: 1 inch = 96 pixels
            const pageWidthPx = 7 * 96; // 672px
            const pageHeightPx = 10 * 96; // 960px
            // On mobile, always single page, no spreads
            const useSpreads = showSpreads && !isMobile;
            // Minimal padding - almost none
            const padding = isMobile ? 4 : 8;
            const gap = useSpreads ? 8 : 0; // Gap between pages in spreads
            // Calculate available space (use almost all of it)
            const availableWidth = containerWidth - (padding * 2);
            const availableHeight = containerHeight - (padding * 2);
            // Calculate scale for single page
            const scaleX = availableWidth / pageWidthPx;
            const scaleY = availableHeight / pageHeightPx;
            const singlePageScale = Math.min(scaleX, scaleY, 1);
            // For spreads (desktop only) - MUST fit both pages
            let finalScale = singlePageScale;
            if (useSpreads) {
                const spreadWidth = (pageWidthPx * 2) + gap;
                const scaleXSpread = availableWidth / spreadWidth;
                // For spreads, we MUST fit width - use the more restrictive scale
                const spreadScale = Math.min(scaleXSpread, scaleY, 1);
                finalScale = spreadScale; // Use spread scale, not single page scale
            }
            // Ensure minimum scale for readability, but allow smaller if needed to fit
            const minScale = isMobile ? 0.2 : 0.25;
            const maxScale = 1;
            setScale(Math.max(minScale, Math.min(finalScale, maxScale)));
        };
        calculateScale();
        window.addEventListener('resize', calculateScale);
        return () => window.removeEventListener('resize', calculateScale);
    }, [showSpreads, isMobile]);
    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Only handle if not typing in an input
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }
            const useSpreads = showSpreads && !isMobile;
            const increment = useSpreads ? 2 : 1;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setPageNumber((prev) => Math.max(1, prev - increment));
            }
            else if (e.key === 'ArrowRight') {
                e.preventDefault();
                const maxPage = pdfUrl
                    ? (useSpreads ? numPages - 1 : numPages)
                    : (pages ? (useSpreads ? pages.length - 1 : pages.length) : 1);
                setPageNumber((prev) => Math.min(maxPage, prev + increment));
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [pageNumber, numPages, pages, showSpreads, pdfUrl, isMobile]);
    // Touch/swipe navigation for mobile
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        touchEndX.current = e.changedTouches[0].clientX;
        handleSwipe();
    };
    const handleSwipe = () => {
        const swipeThreshold = 50;
        const diff = touchStartX.current - touchEndX.current;
        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0) {
                // Swipe left - next page
                goToNextPage();
            }
            else {
                // Swipe right - previous page
                goToPrevPage();
            }
        }
    };
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setLoading(false);
        setError('');
    };
    const onDocumentLoadError = (error) => {
        setError('Failed to load PDF. ' + error.message);
        setLoading(false);
    };
    const goToPrevPage = () => {
        const useSpreads = showSpreads && !isMobile;
        const increment = useSpreads ? 2 : 1;
        setPageNumber((prev) => Math.max(1, prev - increment));
    };
    const goToNextPage = () => {
        const useSpreads = showSpreads && !isMobile;
        const increment = useSpreads ? 2 : 1;
        const maxPage = pdfUrl
            ? (useSpreads ? numPages - 1 : numPages)
            : (pages ? (useSpreads ? pages.length - 1 : pages.length) : 1);
        setPageNumber((prev) => Math.min(maxPage, prev + increment));
    };
    const handleDownload = () => __awaiter(void 0, void 0, void 0, function* () {
        if (pdfUrl) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `${title}.pdf`;
            link.click();
            return;
        }
        // For React components, generate PDF using html2canvas and jsPDF
        if (!pages || pages.length === 0) {
            alert('No content to download');
            return;
        }
        try {
            // Show loading indicator
            const loadingDiv = document.createElement('div');
            loadingDiv.id = 'pdf-loading-indicator';
            loadingDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        color: white;
        font-family: sans-serif;
      `;
            loadingDiv.innerHTML = `
        <div style="text-align: center;">
          <div style="border: 4px solid #f3f3f3; border-top: 4px solid #A0522D; border-radius: 50%; width: 50px; height: 50px; animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
          <p style="font-size: 18px; margin: 0;">Generating PDF...</p>
          <p style="font-size: 14px; margin: 10px 0 0 0; opacity: 0.8;">This may take a moment</p>
        </div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      `;
            document.body.appendChild(loadingDiv);
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [177.8, 254] // 7×10 inches in mm
            });
            // Create a hidden container for rendering pages
            const renderContainer = document.createElement('div');
            renderContainer.style.cssText = `
        position: absolute;
        left: -9999px;
        top: 0;
        width: 672px;
        background: white;
      `;
            document.body.appendChild(renderContainer);
            // Process each page
            for (let i = 0; i < pages.length; i++) {
                // Update loading message
                const statusP = loadingDiv.querySelector('p:last-of-type');
                if (statusP) {
                    statusP.textContent = `Processing page ${i + 1} of ${pages.length}...`;
                }
                // Create a container for this page
                const pageContainer = document.createElement('div');
                pageContainer.style.cssText = `
          width: 672px;
          height: 960px;
          background: #FAF7F2;
          padding: 48px;
          box-sizing: border-box;
          position: relative;
        `;
                renderContainer.appendChild(pageContainer);
                // Render the React component
                const root = createRoot(pageContainer);
                root.render(pages[i]);
                // Wait for render and images to load
                yield new Promise(resolve => setTimeout(resolve, 300));
                // Capture as canvas
                const canvas = yield html2canvas(pageContainer, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#FAF7F2',
                    logging: false,
                    width: 672,
                    height: 960
                });
                // Convert to image
                const imgData = canvas.toDataURL('image/png', 1.0);
                // Add page to PDF (except first page which is created automatically)
                if (i > 0) {
                    pdf.addPage();
                }
                // Calculate dimensions to fit 7×10" (177.8mm x 254mm)
                const imgWidth = 177.8;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                // Add image to PDF
                pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, Math.min(imgHeight, 254), undefined, 'FAST');
                // Clean up this page
                root.unmount();
                renderContainer.removeChild(pageContainer);
            }
            // Clean up
            document.body.removeChild(renderContainer);
            document.body.removeChild(loadingDiv);
            // Save PDF
            pdf.save(`${title}.pdf`);
        }
        catch (error) {
            console.error('PDF generation error:', error);
            const loadingDiv = document.getElementById('pdf-loading-indicator');
            if (loadingDiv) {
                document.body.removeChild(loadingDiv);
            }
            alert('Error generating PDF. Please try using the browser print function (Ctrl/Cmd + P) and save as PDF.');
        }
    });
    const renderCover = () => {
        if (pageNumber === 1 && coverImage) {
            return (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("img", { src: coverImage, alt: `${title} Cover`, className: "max-w-full max-h-full object-contain shadow-2xl rounded-lg" }) }));
        }
        return null;
    };
    const renderBackCover = () => {
        if (pdfUrl && pageNumber >= numPages && backCoverImage) {
            return (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("img", { src: backCoverImage, alt: `${title} Back Cover`, className: "max-w-full max-h-full object-contain shadow-2xl rounded-lg" }) }));
        }
        return null;
    };
    const renderCustomPages = () => {
        if (pages && pages.length > 0) {
            const currentPage = pages[pageNumber - 1];
            const useSpreads = showSpreads && !isMobile && !pdfPreviewMode;
            const nextPage = useSpreads && pageNumber < pages.length ? pages[pageNumber] : null;
            // PDF Preview Mode: Show exact export dimensions with overflow detection
            if (pdfPreviewMode) {
                return (_jsx("div", { className: "w-full h-full overflow-auto bg-gray-200 p-4", style: {
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center'
                    }, children: _jsxs("div", { className: "bg-white shadow-2xl relative", style: {
                            width: '672px',
                            height: '960px',
                            flexShrink: 0,
                            position: 'relative'
                        }, children: [_jsx("div", { style: {
                                    position: 'absolute',
                                    top: '48px',
                                    left: '48px',
                                    right: '48px',
                                    bottom: '48px',
                                    border: '2px dashed #3b82f6',
                                    pointerEvents: 'none',
                                    zIndex: 10
                                }, children: _jsx("div", { className: "absolute -top-6 left-0 text-xs text-blue-600 font-mono bg-white px-1", children: "Usable: 576\u00D7864px (7\u00D710\" minus padding)" }) }), _jsx("div", { style: { position: 'relative', zIndex: 1 }, children: currentPage }), _jsx(OverflowWarning, {})] }) }));
            }
            // Normal preview mode (scaled to fit viewport)
            return (_jsxs("div", { className: "flex items-center justify-center h-full w-full", style: {
                    padding: 0,
                    margin: 0,
                    gap: useSpreads ? '8px' : '0',
                    width: '100%',
                    height: '100%'
                }, onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, children: [_jsx("div", { className: "bg-white shadow-lg overflow-hidden", style: {
                            transform: `scale(${scale})`,
                            transformOrigin: 'center',
                            width: '177.8mm',
                            height: '254mm',
                            flexShrink: 0,
                            borderRadius: isMobile ? '4px' : '8px'
                        }, children: currentPage }), nextPage && useSpreads && (_jsx("div", { className: "bg-white shadow-lg overflow-hidden", style: {
                            transform: `scale(${scale})`,
                            transformOrigin: 'center',
                            width: '177.8mm',
                            height: '254mm',
                            flexShrink: 0,
                            borderRadius: '8px'
                        }, children: nextPage }))] }));
        }
        return null;
    };
    const renderPDFPages = () => {
        if (!pdfUrl)
            return null;
        const currentPage = pageNumber;
        const useSpreads = showSpreads && !isMobile;
        const nextPage = useSpreads && pageNumber < numPages ? pageNumber + 1 : null;
        return (_jsxs("div", { className: "flex items-center justify-center h-full w-full", style: {
                padding: 0,
                margin: 0,
                gap: useSpreads ? '8px' : '0',
                width: '100%',
                height: '100%'
            }, onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, children: [_jsx("div", { className: "shadow-lg", style: { borderRadius: isMobile ? '4px' : '8px' }, children: _jsx(Page, { pageNumber: currentPage, scale: scale, renderTextLayer: true, renderAnnotationLayer: true, className: "border border-gray-200" }) }), nextPage && useSpreads && (_jsx("div", { className: "shadow-lg", style: { borderRadius: '8px' }, children: _jsx(Page, { pageNumber: nextPage, scale: scale, renderTextLayer: true, renderAnnotationLayer: true, className: "border border-gray-200" }) }))] }));
    };
    const maxPage = pdfUrl ? numPages : (pages ? pages.length : 1);
    const useSpreads = showSpreads && !isMobile;
    const isFirstPage = pageNumber === 1;
    const isLastPage = pdfUrl
        ? (useSpreads ? pageNumber >= numPages - 1 : pageNumber >= numPages)
        : (pages ? (useSpreads ? pageNumber >= pages.length - 1 : pageNumber >= pages.length) : true);
    const nextPage = pdfUrl
        ? (useSpreads && pageNumber < numPages ? pageNumber + 1 : null)
        : (pages && useSpreads && pageNumber < pages.length ? pageNumber + 1 : null);
    return (_jsxs("div", { className: "h-screen bg-[#FAF7F2] flex flex-col overflow-hidden", children: [_jsx("div", { className: "bg-white shadow-sm py-2 px-4 md:py-3 md:px-6 flex-shrink-0", children: _jsxs("div", { className: "max-w-7xl mx-auto flex items-center justify-between gap-2", children: [_jsxs(Link, { to: "/printable-library", className: "flex items-center gap-1 md:gap-2 text-[#9A5732] hover:text-[#7A4522] font-body font-semibold transition-colors text-sm md:text-base", children: [_jsx(Home, { className: "w-4 h-4 md:w-5 md:h-5" }), _jsx("span", { className: "hidden sm:inline", children: "Back to Library" }), _jsx("span", { className: "sm:hidden", children: "Back" })] }), _jsx("h1", { className: "text-base md:text-2xl font-heading font-bold text-[#9A5732] text-center flex-1 truncate px-2", children: title }), pages && pages.length > 0 && (_jsxs("label", { className: "flex items-center gap-2 cursor-pointer border-r border-gray-300 pr-3 mr-3", children: [_jsx("input", { type: "checkbox", checked: pdfPreviewMode, onChange: (e) => setPdfPreviewMode(e.target.checked), className: "w-4 h-4 cursor-pointer" }), _jsxs("span", { className: "text-xs md:text-sm font-body text-gray-700 whitespace-nowrap", children: [_jsx("span", { className: "hidden md:inline", children: "PDF Mode" }), _jsx("span", { className: "md:hidden", children: "PDF" })] }), pdfPreviewMode && (_jsx("span", { className: "hidden md:inline text-xs text-orange-600 font-semibold", children: "(Exact)" }))] })), _jsxs("button", { onClick: handleDownload, className: "flex items-center gap-1 md:gap-2 bg-[#9A5732] text-white px-2 md:px-4 py-1.5 md:py-2 rounded-lg hover:bg-[#7A4522] transition-colors font-body font-semibold text-xs md:text-sm whitespace-nowrap", children: [_jsx(Download, { className: "w-4 h-4 md:w-5 md:h-5" }), _jsx("span", { className: "hidden md:inline", children: "Download PDF" })] })] }) }), _jsx("div", { ref: containerRef, className: "flex-1 flex items-center justify-center overflow-hidden relative", style: {
                    minHeight: 0,
                    height: 'calc(100vh - 120px)', // Reduced: Account for header + footer heights
                    width: '100%',
                    padding: 0,
                    margin: 0
                }, tabIndex: 0, role: "region", "aria-label": "Document viewer", children: !pdfUrl && pages && pages.length > 0 ? (renderCustomPages()) : (_jsxs(_Fragment, { children: [loading && (_jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-[#9A5732] mx-auto mb-4" }), _jsx("p", { className: "text-gray-600 font-body", children: "Loading document..." })] })), error && (_jsx("div", { className: "text-center bg-red-50 border border-red-200 rounded-lg p-6 max-w-md", children: _jsx("p", { className: "text-red-600 font-body", children: error }) })), !loading && !error && pdfUrl && (_jsx(Document, { file: pdfUrl, onLoadSuccess: onDocumentLoadSuccess, onLoadError: onDocumentLoadError, loading: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-[#9A5732] mx-auto mb-4" }), _jsx("p", { className: "text-gray-600 font-body", children: "Loading PDF..." })] }), children: renderCover() || renderBackCover() || renderPDFPages() }))] })) }), _jsx("div", { className: "bg-white border-t border-gray-200 py-2 md:py-3 px-4 md:px-6 flex-shrink-0", children: _jsxs("div", { className: "max-w-7xl mx-auto flex items-center justify-between gap-2", children: [_jsxs("button", { onClick: goToPrevPage, disabled: isFirstPage, className: `flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 rounded-lg font-body font-semibold transition-all text-sm md:text-base ${isFirstPage
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-[#9A5732] text-white hover:bg-[#7A4522] active:scale-95'}`, children: [_jsx(ChevronLeft, { className: "w-4 h-4 md:w-5 md:h-5" }), _jsx("span", { className: "hidden sm:inline", children: "Previous" }), _jsx("span", { className: "sm:hidden", children: "Prev" })] }), _jsxs("div", { className: "text-center flex-1 px-2", children: [_jsxs("p", { className: "text-gray-700 font-body text-sm md:text-base", children: [pageNumber, nextPage ? `-${nextPage}` : '', " / ", maxPage] }), !isMobile && (_jsx("p", { className: "text-xs text-gray-500 font-body mt-1", children: "Arrow keys or swipe" })), isMobile && (_jsx("p", { className: "text-xs text-gray-500 font-body mt-1", children: "Swipe to navigate" }))] }), _jsxs("button", { onClick: goToNextPage, disabled: isLastPage, className: `flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 rounded-lg font-body font-semibold transition-all text-sm md:text-base ${isLastPage
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-[#9A5732] text-white hover:bg-[#7A4522] active:scale-95'}`, children: [_jsx("span", { className: "hidden sm:inline", children: "Next" }), _jsx("span", { className: "sm:hidden", children: "Next" }), _jsx(ChevronRight, { className: "w-4 h-4 md:w-5 md:h-5" })] })] }) })] }));
};
export default BookViewer;
